import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, PointerEvent } from "react";
import { useTranslation } from "react-i18next";

import { MkButton } from "./MkButton";
import { MkField } from "./MkField";

const PEN_WIDTH = 2.2;
const PEN_COLOR = "#0f172a";

interface Props {
    value?: string | false;
    src?: string | false;
    label?: string;
    hint?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    onUpdate?(value: string | false): void;
}

export function MkSignature({ value, src, label, hint, error, required, disabled, className,
                              onUpdate }: Props) {
    const { t } = useTranslation();
    const canvas = useRef<HTMLCanvasElement>(null);
    const file = useRef<HTMLInputElement>(null);
    const context = useRef<CanvasRenderingContext2D | null>(null);
    const drawing = useRef(false);
    const [mode, setMode] = useState<"preview" | "draw">(src || value ? "preview" : "draw");
    const [drawn, setDrawn] = useState(false);

    const previewSrc = src || value || "";

    const fit = useCallback(() => {
        const node = canvas.current;
        if (!node) {
            return;
        }
        const ratio = window.devicePixelRatio || 1;
        const rect = node.getBoundingClientRect();
        node.width = Math.max(1, Math.round(rect.width * ratio));
        node.height = Math.max(1, Math.round(rect.height * ratio));
        const ctx = node.getContext("2d");
        if (!ctx) {
            return;
        }
        ctx.scale(ratio, ratio);
        ctx.lineWidth = PEN_WIDTH;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = PEN_COLOR;
        context.current = ctx;
    }, []);

    useEffect(() => {
        if (mode === "draw") {
            fit();
        }
    }, [mode, fit]);

    useEffect(() => {
        const up = () => {
            drawing.current = false;
        };
        window.addEventListener("pointerup", up);
        return () => window.removeEventListener("pointerup", up);
    }, []);

    function point(event: PointerEvent<HTMLCanvasElement>) {
        const rect = event.currentTarget.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    function start(event: PointerEvent<HTMLCanvasElement>) {
        if (disabled || !context.current) {
            return;
        }
        event.preventDefault();
        drawing.current = true;
        setDrawn(true);
        const { x, y } = point(event);
        context.current.beginPath();
        context.current.moveTo(x, y);
    }

    function move(event: PointerEvent<HTMLCanvasElement>) {
        if (!drawing.current || !context.current) {
            return;
        }
        event.preventDefault();
        const { x, y } = point(event);
        context.current.lineTo(x, y);
        context.current.stroke();
    }

    function clear() {
        const node = canvas.current;
        if (context.current && node) {
            context.current.clearRect(0, 0, node.width, node.height);
        }
        setDrawn(false);
    }

    function apply() {
        if (!drawn || !canvas.current) {
            return;
        }
        onUpdate?.(canvas.current.toDataURL("image/png"));
        setMode("preview");
    }

    function onFile(event: ChangeEvent<HTMLInputElement>) {
        const picked = event.target.files?.[0];
        event.target.value = "";
        if (!picked) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            onUpdate?.(String(reader.result));
            setMode("preview");
        };
        reader.readAsDataURL(picked);
    }

    function redraw() {
        setMode("draw");
        setDrawn(false);
        requestAnimationFrame(() => {
            fit();
            clear();
        });
    }

    return (
        <MkField label={label} hint={hint} error={error} required={required}
                 className={`mk-sign ${className ?? ""}`}>
            <input type="file" accept="image/*" className="mk-sign-input" ref={file}
                   onChange={onFile} />
            {mode === "preview" && previewSrc ? (
                <>
                    <div className="mk-sign-preview">
                        <img src={previewSrc} alt={label || ""} decoding="async" />
                    </div>
                    {!disabled && (
                        <div className="mk-sign-bar">
                            <MkButton variant="ghost" size="sm" icon="fa-pencil"
                                      label={t("mk.sign.replace")} onClick={redraw} />
                            <MkButton variant="ghost" size="sm" icon="fa-trash"
                                      label={t("mk.sign.remove")}
                                      onClick={() => {
                                          onUpdate?.(false);
                                          redraw();
                                      }} />
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className={`mk-sign-pad${disabled ? " is-disabled" : ""}`}>
                        <canvas ref={canvas} className="mk-sign-canvas"
                                onPointerDown={start} onPointerMove={move}
                                onPointerUp={() => { drawing.current = false; }}
                                onPointerLeave={() => { drawing.current = false; }} />
                        {!drawn && <span className="mk-sign-hint">{t("mk.sign.placeholder")}</span>}
                    </div>
                    {!disabled && (
                        <div className="mk-sign-bar">
                            <MkButton variant="primary" size="sm" icon="fa-check"
                                      label={t("mk.sign.save")} disabled={!drawn} onClick={apply} />
                            <MkButton variant="ghost" size="sm" icon="fa-eraser"
                                      label={t("mk.sign.clear")} onClick={clear} />
                            <MkButton variant="ghost" size="sm" icon="fa-upload"
                                      label={t("mk.sign.upload")}
                                      onClick={() => file.current?.click()} />
                        </div>
                    )}
                </>
            )}
        </MkField>
    );
}
